import { useEffect, useState, useRef } from 'react';

import { AircraftOverviewCard } from '@/features/iata-binder/components/AircraftOverviewCard';
import { SimplifiedIATABinder } from '@/features/iata-binder/components/SimplifiedIATABinder';
import {
    fetchAllPaginatedItems,
    listComponents,
    useComplianceProjectSummary,
    useProject,
} from '@/features/end-of-lease/api';
import { ComponentWithInstalledItem } from '@/features/end-of-lease/types';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Upload } from 'lucide-react';

import { LIST_VIEW_RETURN_KEY } from '@/lib/listViewState';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { LoadingScreen } from '@/components/loading-screen';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/iata-binder/projects/$projectId/page')({
    component: IataBinderProjectDetailPage,
});

function IataBinderProjectDetailPage() {
    const navigate = Route.useNavigate();
    const { projectId: _projectId } = Route.useParams();
    const projectId = Number(_projectId);

    const { client } = useAuthAxios();
    const [allComponents, setAllComponents] = useState<
        ComponentWithInstalledItem[]
    >([]);
    const hasFetchedComponents = useRef(false);

    const { data: complianceSummary } = useComplianceProjectSummary(projectId);

    useEffect(() => {
        // Prevent duplicate fetches
        if (hasFetchedComponents.current) return;
        if (!client) return;

        hasFetchedComponents.current = true;

        const fetchAllComponents = async () => {
            const allFetchedItems =
                await fetchAllPaginatedItems<ComponentWithInstalledItem>(
                    ({ limit, skip }) =>
                        listComponents(client, projectId, limit, skip),
                );
            setAllComponents(allFetchedItems);
        };

        fetchAllComponents();
    }, [projectId, client]);

    const { data: project, isLoading: isProjectLoading } =
        useProject(projectId);

    // Asks the dashboard to restore its filters (cast: the route's validator doesn't know `_lvReturn`).
    const handleBack = () => {
        navigate({
            to: '/iata-binder',
            search: { [LIST_VIEW_RETURN_KEY]: true },
        } as unknown as Parameters<typeof navigate>[0]);
    };

    const navigateToFolder = (letter: string) => {
        navigate({
            to: '/iata-binder/projects/$projectId/folders/$folderLetter',
            params: {
                projectId: projectId.toString(),
                folderLetter: letter,
            },
        });
    };

    if (!project || isProjectLoading) {
        return <LoadingScreen />;
    }

    const aircraftType =
        project?.aircraftType || project?.leasingAircraft?.type || '-';
    const registration = project?.leasingAircraft?.registration || '-';

    return (
        <div
            className="flex-1 min-h-full bg-muted/50"
            data-testid="IataBinderProjectDetail"
        >
            {/* White background header section - full width */}
            <div className="bg-white shadow-xs">
                <div className="px-8 py-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleBack}
                                className="text-muted-foreground hover:text-foreground -ml-2"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                        </div>
                        <Link
                            to="/iata-binder/projects/$projectId/upload"
                            params={{ projectId: _projectId }}
                        >
                            <Button
                                variant="outline"
                                size="sm"
                                className="border-primary text-primary hover:bg-primary/10"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Documents
                            </Button>
                        </Link>
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {project.title}
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {aircraftType} - {registration}
                        </p>
                    </div>
                </div>
            </div>

            {/* Gray background body section */}
            <div className="p-8 space-y-6">
                <AircraftOverviewCard
                    project={project}
                    components={allComponents}
                />
                <SimplifiedIATABinder
                    complianceSummary={complianceSummary}
                    navigateToFolder={navigateToFolder}
                />
            </div>
        </div>
    );
}
